import { motion } from 'framer-motion'
import { useInView } from './useInView'
import {
  Package,
  Calendar,
  ShoppingCart,
  Workflow,
  Sparkles,
  TrendingUp,
  Settings,
  LineChart,
  Network,
} from 'lucide-react'
import type { LucideIcon } from 'lucide-react'
import { useT } from '../i18n/LanguageContext'
import type { Dictionary } from '../i18n/types'

type PillarKey = keyof Dictionary['oferta']['pillars']
type SaasItemKey = keyof Dictionary['oferta']['pillars']['saas']['items']
type TransformacjeItemKey = keyof Dictionary['oferta']['pillars']['transformacje']['items']
type AiItemKey = keyof Dictionary['oferta']['pillars']['ai']['items']

interface PillarMeta {
  key: PillarKey
  icon: LucideIcon
  items: Array<{ key: string; icon: LucideIcon }>
}

const pillarsMeta: PillarMeta[] = [
  {
    key: 'saas',
    icon: Package,
    items: [
      { key: 'scheduler' satisfies SaasItemKey, icon: Calendar },
      { key: 'sklep' satisfies SaasItemKey, icon: ShoppingCart },
    ],
  },
  {
    key: 'transformacje',
    icon: Workflow,
    items: [
      { key: 'sales' satisfies TransformacjeItemKey, icon: TrendingUp },
      { key: 'operations' satisfies TransformacjeItemKey, icon: Settings },
    ],
  },
  {
    key: 'ai',
    icon: Sparkles,
    items: [
      { key: 'analytics' satisfies AiItemKey, icon: LineChart },
      { key: 'integrations' satisfies AiItemKey, icon: Network },
    ],
  },
]

export default function Oferta() {
  const t = useT()
  const { ref, isInView } = useInView()

  return (
    <section
      id="oferta"
      className="relative pt-8 pb-24 min-h-[88vh] flex flex-col justify-center overflow-hidden"
    >
      <div ref={ref} className="relative z-10 max-w-7xl mx-auto px-6">
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.7 }}
          className="text-center max-w-3xl mx-auto mb-8"
        >
          <p className="font-mono text-brand-400 text-xs tracking-[0.2em] uppercase mb-3">
            {t.oferta.eyebrow}
          </p>
          <h2 className="font-display text-4xl sm:text-5xl font-bold text-white tracking-tight mb-4">
            {t.oferta.titlePart1}{' '}
            <span className="text-gradient-warm">{t.oferta.titlePart2}</span>
          </h2>
          <p className="text-base text-zinc-400 leading-relaxed">
            {t.oferta.mission}
          </p>
        </motion.div>

        <div className="grid lg:grid-cols-3 gap-5 max-w-6xl mx-auto">
          {pillarsMeta.map((pillar, i) => {
            const pillarT = t.oferta.pillars[pillar.key]
            const PillarIcon = pillar.icon
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            const items = pillarT.items as Record<string, { title: string; desc: string }>
            return (
              <motion.div
                key={pillar.key}
                initial={{ opacity: 0, y: 30 }}
                animate={isInView ? { opacity: 1, y: 0 } : {}}
                transition={{ duration: 0.6, delay: 0.15 + i * 0.15 }}
                className="group relative p-5 rounded-xl bg-white/[0.05] border border-white/[0.08] hover:border-white/[0.14] transition-all hover:bg-white/[0.08] flex flex-col"
              >
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-9 h-9 rounded-lg bg-brand-600/10 flex items-center justify-center shrink-0 group-hover:bg-brand-600/20 transition-colors">
                    <PillarIcon size={18} className="text-brand-400" />
                  </div>
                  <h3 className="font-display text-lg font-bold text-white">
                    {pillarT.title}
                  </h3>
                </div>

                <p className="text-sm text-zinc-400 leading-relaxed">
                  {pillarT.description}
                </p>

                <div className="mt-5 pt-4 border-t border-white/[0.06] space-y-3">
                  {pillar.items.map((item) => {
                    const itemContent = items[item.key]
                    const ItemIcon = item.icon
                    return (
                      <div key={item.key} className="flex items-start gap-3">
                        <div className="w-7 h-7 rounded-lg border border-accent-500/30 flex items-center justify-center shrink-0 mt-0.5">
                          <ItemIcon size={13} className="text-accent-400" />
                        </div>
                        <div className="min-w-0">
                          <h4 className="font-display text-sm font-semibold text-white mb-0.5">
                            {itemContent.title}
                          </h4>
                          <p className="text-xs text-zinc-500 leading-relaxed">
                            {itemContent.desc}
                          </p>
                        </div>
                      </div>
                    )
                  })}
                </div>
              </motion.div>
            )
          })}
        </div>
      </div>
    </section>
  )
}
